import { create } from 'zustand';
import { toast } from 'sonner';
import type { AgentStep, CrmAgentResult, CrmTask, SharedPreferences } from '@dts/core';
import type { CrmApprovalRequest } from '@/bridge/types';
import { queryClient } from './query-client';
import { qk } from './queries';

export interface CrmRun {
  id: string;
  title: string;
  kind: CrmTask['kind'];
  status: 'running' | 'done' | 'error';
  steps: AgentStep[];
  summary?: string;
  wrote?: boolean;
  startedAt: number;
  relatedId?: string;
}

interface CrmState {
  runs: CrmRun[];
  approvals: CrmApprovalRequest[];
  panelOpen: boolean;
  setPanel(open: boolean): void;
}

export const useCrm = create<CrmState>((set) => ({
  runs: [],
  approvals: [],
  panelOpen: false,
  setPanel: (panelOpen) => set({ panelOpen }),
}));

let wired = false;
export function wireCrmEvents() {
  if (wired) return;
  wired = true;
  window.dts.crm.onStep((runId, step) =>
    useCrm.setState((s) => ({ runs: s.runs.map((r) => (r.id === runId ? { ...r, steps: [...r.steps, step] } : r)) })),
  );
  window.dts.crm.onApproval((req) => useCrm.setState((s) => ({ approvals: [...s.approvals, req] })));
}

export function answerApproval(requestId: string, approved: boolean) {
  window.dts.crm.respond(requestId, approved);
  useCrm.setState((s) => ({ approvals: s.approvals.filter((a) => a.requestId !== requestId) }));
}

function mode(): 'ask' | 'auto' {
  const m = queryClient.getQueryData<SharedPreferences>(qk.prefs)?.crm?.mode;
  return m === 'auto' ? 'auto' : 'ask';
}

/** Runs a CRM task through the main process agent, tracking progress for the activity panel. */
export async function runCrmTask(task: CrmTask, title: string, relatedId?: string): Promise<CrmAgentResult | null> {
  const id = `crm-${Date.now()}`;
  useCrm.setState((s) => ({ runs: [{ id, title, kind: task.kind, status: 'running' as const, steps: [], startedAt: Date.now(), relatedId } satisfies CrmRun, ...s.runs].slice(0, 50) }));
  const tid = task.kind === 'log' ? toast.loading(title, { description: 'AI is finding the right CRM record…' }) : undefined;
  const r = await window.dts.crm.run(task, { mode: mode(), runId: id });
  if (r.ok) {
    useCrm.setState((s) => ({ runs: s.runs.map((x) => (x.id === id ? { ...x, status: 'done' as const, summary: r.data.summary, wrote: r.data.wrote } : x)) }));
    if (tid !== undefined) {
      if (r.data.wrote) toast.success('Logged to CRM', { id: tid, description: r.data.summary, duration: 6000 });
      else toast.message('CRM: nothing logged', { id: tid, description: r.data.summary, duration: 7000 });
    }
    return r.data;
  }
  useCrm.setState((s) => ({ runs: s.runs.map((x) => (x.id === id ? { ...x, status: 'error' as const, summary: r.error } : x)) }));
  if (tid !== undefined) toast.error('CRM logging failed', { id: tid, description: r.error, duration: 8000 });
  return null;
}
